import React, { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { WebsiteLinkIcon, FromScratchIcon, LinkIcon, DocumentIcon } from '../../images';
import styles from './knowledge-add-dropdown.module.scss';
import { getLang } from 'shared/store/selectors/lang.selector';
import analyticsService from '../../helpers/analytics.service';
import { EVENTS, DATA_KEYS, Lang } from 'shared/constants';
import { getOwnersByIDs } from 'shared/store/selectors/owners.selector';
import { getUser } from 'shared/store/selectors/user.selector';
import { Dropdown } from '../design-system';

/**
 * KnowledgeAddDropdown - Dropdown menu for adding knowledge items
 * Provides options to create new, link existing, upload file, or add external link
 */
function KnowledgeAddDropdown({
  onCreate = () => {},
  onLink = () => {},
  onUpload = () => {},
  onExternalLink = () => {},
  title = '',
  ownerIds = [],
  buttonText = null,
}) {
  const lang = useSelector(getLang('KNOWLEDGE_ADD_POPUP'));
  const langButtons = Lang.KNOWLEDGE_BUTTONS;
  const owners = useSelector(getOwnersByIDs(ownerIds));
  const { email: userEmail } = useSelector(getUser);
  const ownersEmails = useMemo(() => owners?.map(owner => owner.email) || [], [owners]);

  const trackAndExecute = useCallback(
    (type, callback) => {
      analyticsService.track(EVENTS.KNOWLEDGE_ITEMS.CREATE_NEW_ITEM_BUTTON, {
        type,
        title,
        ownersEmails,
        userEmail,
      });
      callback();
    },
    [title, ownersEmails, userEmail],
  );

  const dropdownItems = useMemo(
    () => [
      {
        key: 'new',
        label: (
          <div className={styles.dropdownItem}>
            <img className={styles.dropdownIcon} src={FromScratchIcon} alt="" />
            <div>
              <div className={styles.dropdownItemTitle}>{lang.NEW_TITLE}</div>
              <div className={styles.dropdownItemDescription}>{lang.NEW_DESCRIPTION}</div>
            </div>
          </div>
        ),
        onClick: () => trackAndExecute('newItem', onCreate),
      },
      {
        key: 'link',
        label: (
          <div className={styles.dropdownItem}>
            <img className={styles.dropdownIconSmall} src={LinkIcon} alt="" />
            <div>
              <div className={styles.dropdownItemTitle}>{lang.LINK_TITLE}</div>
              <div className={styles.dropdownItemDescription}>{lang.LINK_DESCRIPTION}</div>
            </div>
          </div>
        ),
        onClick: () => trackAndExecute('linkExistingItem', onLink),
      },
      {
        key: 'upload',
        label: (
          <div className={styles.dropdownItem}>
            <img className={styles.dropdownIcon} src={DocumentIcon} alt="" />
            <div>
              <div className={styles.dropdownItemTitle}>{lang.UPLOAD_TITLE}</div>
              <div className={styles.dropdownItemDescription}>{lang.UPLOAD_DESCRIPTION}</div>
            </div>
          </div>
        ),
        onClick: () => trackAndExecute('uploadFile', onUpload),
      },
      {
        key: 'external',
        label: (
          <div className={styles.dropdownItem}>
            <img className={styles.dropdownIconSmall} src={WebsiteLinkIcon} alt="" />
            <div>
              <div className={styles.dropdownItemTitle}>{lang.EXTERNAL_TITLE}</div>
              <div className={styles.dropdownItemDescription}>{lang.EXTERNAL_DESCRIPTION}</div>
            </div>
          </div>
        ),
        onClick: () => trackAndExecute('link', onExternalLink),
      },
    ],
    [lang, trackAndExecute, onCreate, onLink, onUpload, onExternalLink],
  );

  return (
    <Dropdown
      items={dropdownItems}
      trigger={['click']}
      placement="bottomLeft"
      data-key={DATA_KEYS.KNOWLEDGE_BASE.ADD.BUTTON}
      childrenWrapperStyle={styles.primaryTrigger}
      data-testid="knowledge-binder-add-item-button"
    >
      <span>{buttonText || langButtons.ADD}</span>
    </Dropdown>
  );
}

export { KnowledgeAddDropdown };
